import { X } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import { useCatalogs, useTasks } from '@/hooks/useTasks';
import { emptyFilters, DATE_FIELD_LABELS } from '@/types';
import type { DateField } from '@/types';
import { useSitePlanCatalogs } from '@/hooks/useSitePlan';
import { StatusSelect } from '@/components/common/StatusSelect';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import type { AuthFilterValue, ActivoFilterValue } from '@/types';

export function FilterPanel() {
  const location = useLocation();
  const isSitePlan = location.pathname === '/site-plan';

  if (isSitePlan) return <SitePlanFilters />;
  return <TaskFilters />;
}

// ----------------------------------------------------------------------
// FILTROS DE TAREAS (panel original)
// ----------------------------------------------------------------------
function TaskFilters() {
  const filtersOpen = useAppStore((s) => s.filtersOpen);
  const setFiltersOpen = useAppStore((s) => s.setFiltersOpen);
  const filters = useAppStore((s) => s.filters);
  const setFilter = useAppStore((s) => s.setFilter);
  const resetFilters = useAppStore((s) => s.resetFilters);
  const groupBy = useAppStore((s) => s.groupBy);
  const search = useAppStore((s) => s.search);
  const { data: catalogs } = useCatalogs();
  const { data: tasks } = useTasks(groupBy, search);

  // Derive available years from the task data based on the currently selected date field.
  // This way the year list shows only years that actually have data for the chosen field.
  const availableYears = useMemo(() => {
    if (!tasks) return [];
    const yearsSet = new Set<number>();
    for (const t of tasks) {
      const dateStr = (t as any)[filters.dateField];
      if (dateStr) {
        const year = new Date(dateStr).getFullYear();
        if (!isNaN(year) && year > 1900 && year < 3000) yearsSet.add(year);
      }
    }
    return Array.from(yearsSet).sort((a, b) => b - a); // most recent first
  }, [tasks, filters.dateField]);

  if (!catalogs) return null;

  const activeCount = Object.entries(filters).filter(([k, v]) => {
  // El SegmentedControl tiene siempre un valor; cuenta solo si no es 'all'.
  if (k === 'authStatus') return v !== 'all';
  // activoStatus default es 'active' (oculta inactivas). Solo cuenta como
  // filtro activo cuando el usuario lo cambia a 'inactive' o 'all'.
  if (k === 'activoStatus') return v !== 'active';
  // dateField always has a value (default 'fechaInicio'), so it shouldn't count as active.
  // The actual date filter is "active" only when filterYear is set.
  if (k === 'dateField') return false;
  if (k === 'filterMonth') return false; // counted via filterYear (you can't have month without year)
  return v !== null;
}).length;

  return (
    <>
      {filtersOpen && (
        <div
          onClick={() => setFiltersOpen(false)}
          className="fixed top-[var(--alto-arriba)] left-0 right-0 bottom-[var(--alto-abajo)] bg-black/20 z-40 transition-opacity"
        />
      )}
      <aside
        className={`fixed top-[var(--alto-arriba)] right-0 bottom-[var(--alto-abajo)] w-[340px] bg-white border-l border-stone-200 shadow-xl z-50 transform transition-transform duration-200 ${
          filtersOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-medium">Filtros</h2>
          <button
            onClick={() => setFiltersOpen(false)}
            className="text-stone-500 hover:text-stone-900 transition-colors p-1 rounded hover:bg-stone-100"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - var(--alto-arriba) - var(--alto-abajo) - 6rem)' }}>
          <div className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
            Tarea
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Estado</label>
            <StatusSelect
              statuses={catalogs.statuses}
              value={filters.estadoId}
              onChange={(id) => setFilter('estadoId', id === 0 ? null : id)}
              includeAllOption
              placeholder="— Todos —"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Proyecto</label>
            <select
              value={filters.proyectoId ?? ''}
              onChange={(e) => setFilter('proyectoId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {catalogs.projects.map((p) => (
                <option key={p.idProyecto} value={p.idProyecto}>{p.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Categoría</label>
            <select
              value={filters.categoriaId ?? ''}
              onChange={(e) => setFilter('categoriaId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todas —</option>
              {catalogs.categories.map((c) => (
                <option key={c.idCatTareaArq} value={c.idCatTareaArq}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Sprint</label>
            <select
              value={filters.sprintId ?? ''}
              onChange={(e) => setFilter('sprintId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {catalogs.sprints.map((s) => (
                <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-stone-600">
              Visibilidad
            </label>
            <SegmentedControl<ActivoFilterValue>
              options={[
                { value: 'active', label: 'Activas' },
                { value: 'inactive', label: 'Inactivas' },
                { value: 'all', label: 'Todas' },
              ]}
              value={filters.activoStatus}
              onChange={(v) => setFilter('activoStatus', v)}
              className="w-full"
            />
          </div>

          <div className="text-xs font-semibold text-stone-700 uppercase tracking-wide pt-4 border-t border-stone-200">
            Involucrados
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Asignado</label>
            <select
              value={filters.asignadoId === 'unassigned' ? '__unassigned' : (filters.asignadoId ?? '')}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '__unassigned') setFilter('asignadoId', 'unassigned');
                else if (val === '') setFilter('asignadoId', null);
                else setFilter('asignadoId', Number(val));
              }}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              <option value="__unassigned">Sin Asignar</option>
              {catalogs.users.map((u) => (
                <option key={u.idCol} value={u.idCol}>{u.nombre || u.nombreCompleto}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Autorizador</label>
            <select
              value={filters.autorizadorId === 'unassigned' ? '__unassigned' : (filters.autorizadorId ?? '')}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '__unassigned') setFilter('autorizadorId', 'unassigned');
                else if (val === '') setFilter('autorizadorId', null);
                else setFilter('autorizadorId', Number(val));
              }}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              <option value="__unassigned">Sin Asignar</option>
              {catalogs.users.map((u) => (
                <option key={u.idCol} value={u.idCol}>{u.nombre || u.nombreCompleto}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-stone-600">
              Estado de autorización
            </label>
            <SegmentedControl<AuthFilterValue>
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'authorized', label: 'Autorizadas' },
                { value: 'unauthorized', label: 'Sin autorizar' },
              ]}
              value={filters.authStatus}
              onChange={(v) => setFilter('authStatus', v)}
              className="w-full"
            />
          </div>

          {/* Filtro por fecha */}
<div className="border-t border-stone-200 pt-4">
  <div className="flex items-center justify-between mb-3">
    <div className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
      Filtrar por fecha
    </div>
    <button
      type="button"
      onClick={() => {
        const now = new Date();
        setFilter('filterYear', now.getFullYear());
        setFilter('filterMonth', now.getMonth() + 1); // JS is 0-indexed; we use 1-12
      }}
      className="text-[11px] font-medium text-brand-gray-400 hover:text-brand-gray-500 underline-offset-2 hover:underline"
    >
      Mes actual
    </button>
  </div>

  <div className="mb-3">
    <label className="block text-xs font-medium text-stone-600 mb-1.5">Tipo de fecha</label>
    <select
      value={filters.dateField}
      onChange={(e) => setFilter('dateField', e.target.value as DateField)}
      className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      {(Object.keys(DATE_FIELD_LABELS) as DateField[]).map((key) => (
        <option key={key} value={key}>{DATE_FIELD_LABELS[key]}</option>
      ))}
    </select>
  </div>

  <div className="grid grid-cols-2 gap-3">
    <div>
      <label className="block text-xs font-medium text-stone-600 mb-1.5">Año</label>
      <select
        value={filters.filterYear ?? ''}
        onChange={(e) => {
          const newYear = e.target.value ? Number(e.target.value) : null;
          setFilter('filterYear', newYear);
          // If clearing the year, also clear the month (month requires a year).
          if (newYear === null) setFilter('filterMonth', null);
        }}
        className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">— Cualquiera —</option>
        {availableYears.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>

    <div>
      <label
        className={`block text-xs font-medium mb-1.5 ${
          filters.filterYear === null ? 'text-stone-400' : 'text-stone-600'
        }`}
      >
        Mes
      </label>
      <select
        value={filters.filterMonth ?? ''}
        onChange={(e) =>
          setFilter('filterMonth', e.target.value ? Number(e.target.value) : null)
        }
        disabled={filters.filterYear === null}
        className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-stone-100 disabled:text-stone-400 disabled:cursor-not-allowed"
      >
        <option value="">— Cualquiera —</option>
        <option value="1">Enero</option>
        <option value="2">Febrero</option>
        <option value="3">Marzo</option>
        <option value="4">Abril</option>
        <option value="5">Mayo</option>
        <option value="6">Junio</option>
        <option value="7">Julio</option>
        <option value="8">Agosto</option>
        <option value="9">Septiembre</option>
        <option value="10">Octubre</option>
        <option value="11">Noviembre</option>
        <option value="12">Diciembre</option>
      </select>
    </div>
  </div>
</div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-5 py-3 border-t border-stone-200 bg-white flex gap-2">
          <button
            onClick={resetFilters}
            className="flex-1 px-4 py-2 border border-stone-300 rounded-md text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            Limpiar
          </button>
          <button
            onClick={() => setFiltersOpen(false)}
            className="flex-[2] px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            Aplicar {activeCount > 0 && `(${activeCount})`}
          </button>
        </div>
      </aside>
    </>
  );
}

// ----------------------------------------------------------------------
// FILTROS DE SITIO
// ----------------------------------------------------------------------
function SitePlanFilters() {
  const filtersOpen = useAppStore((s) => s.filtersOpen);
  const setFiltersOpen = useAppStore((s) => s.setFiltersOpen);
  const filters = useAppStore((s) => s.sitePlanFilters);
  const setFilter = useAppStore((s) => s.setSitePlanFilter);
  const resetFilters = useAppStore((s) => s.resetSitePlanFilters);
  const proyecto = useAppStore((s) => s.sitePlanProject);

  const {
    estados,
    modelos,
    vendedores,
    bancos,
    formalizadores,
    frentes,
    fondos,
  } = useSitePlanCatalogs(proyecto);

  const activeCount =
    (filters.estado !== null ? 1 : 0) +
    (filters.modelo !== null ? 1 : 0) +
    (filters.vendedor !== null ? 1 : 0) +
    (filters.banco !== null ? 1 : 0) +
    (filters.formalizador !== null ? 1 : 0) +
    (filters.frente !== null ? 1 : 0) +
    (filters.fondo !== null ? 1 : 0) +
    (filters.enConstruccion ? 1 : 0) +
    (filters.conPermiso ? 1 : 0);

  return (
    <>
      {filtersOpen && (
        <div
          onClick={() => setFiltersOpen(false)}
          className="fixed top-[var(--alto-arriba)] left-0 right-0 bottom-[var(--alto-abajo)] bg-black/20 z-40 transition-opacity"
        />
      )}
      <aside
        className={`fixed top-[var(--alto-arriba)] right-0 bottom-[var(--alto-abajo)] w-[340px] bg-white border-l border-stone-200 shadow-xl z-50 transform transition-transform duration-200 ${
          filtersOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-medium">Filtros del plano</h2>
          <button
            onClick={() => setFiltersOpen(false)}
            className="text-stone-500 hover:text-stone-900 transition-colors p-1 rounded hover:bg-stone-100"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div
          className="px-5 py-4 space-y-4 overflow-y-auto"
          style={{ maxHeight: 'calc(100dvh - var(--alto-arriba) - var(--alto-abajo) - 140px)' }}
        >
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Estado</label>
            <select
              value={filters.estado ?? ''}
              onChange={(e) => setFilter('estado', e.target.value || null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {estados.map((e: string) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Modelo</label>
            <select
              value={filters.modelo ?? ''}
              onChange={(e) => setFilter('modelo', e.target.value || null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {modelos.map((m: string) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Vendedor</label>
            <select
              value={filters.vendedor ?? ''}
              onChange={(e) => setFilter('vendedor', e.target.value || null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {vendedores.map((v: string) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Banco</label>
            <select
              value={filters.banco ?? ''}
              onChange={(e) => setFilter('banco', e.target.value || null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {bancos.map((b: string) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Formalizador</label>
            <select
              value={filters.formalizador ?? ''}
              onChange={(e) => setFilter('formalizador', e.target.value || null)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">— Todos —</option>
              {formalizadores.map((f: string) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Frente</label>
              <select
                value={filters.frente ?? ''}
                onChange={(e) =>
                  setFilter('frente', e.target.value ? Number(e.target.value) : null)
                }
                className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Todos —</option>
                {frentes.map((f: number) => (
                  <option key={f} value={f}>{f.toFixed(2)} m</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Fondo</label>
              <select
                value={filters.fondo ?? ''}
                onChange={(e) =>
                  setFilter('fondo', e.target.value ? Number(e.target.value) : null)
                }
                className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Todos —</option>
                {fondos.map((f: number) => (
                  <option key={f} value={f}>{f.toFixed(2)} m</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center justify-between px-3 py-2.5 bg-stone-50 rounded-md cursor-pointer hover:bg-stone-100 transition-colors">
            <span className="text-sm text-stone-700">🚧 En Construcción</span>
            <input
              type="checkbox"
              checked={filters.enConstruccion}
              onChange={(e) => setFilter('enConstruccion', e.target.checked)}
              className="w-4 h-4 accent-emerald-600 cursor-pointer"
            />
          </label>

          <label className="flex items-center justify-between px-3 py-2.5 bg-stone-50 rounded-md cursor-pointer hover:bg-stone-100 transition-colors">
            <span className="text-sm text-stone-700">📋 Con Permiso</span>
            <input
              type="checkbox"
              checked={filters.conPermiso}
              onChange={(e) => setFilter('conPermiso', e.target.checked)}
              className="w-4 h-4 accent-emerald-600 cursor-pointer"
            />
          </label>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-5 py-3 border-t border-stone-200 bg-white flex gap-2">
          <button
            onClick={resetFilters}
            className="flex-1 px-4 py-2 border border-stone-300 rounded-md text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            Limpiar
          </button>
          <button
            onClick={() => setFiltersOpen(false)}
            className="flex-[2] px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            Aplicar {activeCount > 0 && `(${activeCount})`}
          </button>
        </div>
      </aside>
    </>
  );
}

// ----------------------------------------------------------------------
// applyFilters de tareas (sin cambios respecto al original)
// ----------------------------------------------------------------------
export function applyFilters<T extends {
  idEstado?: number | null;
  idProyecto?: number | null;
  idCategoria?: number | null;
  responsable?: number | null;
  tgCompletado?: boolean | null;
  tgAutorizadoInt?: boolean | null;
  idAutorizadoInt?: number | null;
  esActivo?: boolean;
  idSprint?: number | null;
  fechaInicio?: string | null;
  fechaProyectada?: string | null;
  fechaCompletado?: string | null;
  fechaCreacion?: string | null;
}>(tasks: T[], filters: typeof emptyFilters): T[] {
  return tasks.filter((t) => {
    // Soft-delete: default 'active' oculta inactivas. Tareas sin la propiedad
    // (en transito durante una migracion de tipos) se consideran activas.
    if (filters.activoStatus === 'active' && t.esActivo === false) return false;
    if (filters.activoStatus === 'inactive' && t.esActivo !== false) return false;

    if (filters.estadoId !== null && t.idEstado !== filters.estadoId) return false;
    if (filters.proyectoId !== null && t.idProyecto !== filters.proyectoId) return false;
    if (filters.categoriaId !== null && t.idCategoria !== filters.categoriaId) return false;
    if (filters.sprintId !== null && t.idSprint !== filters.sprintId) return false;

    if (filters.asignadoId !== null) {
      if (filters.asignadoId === 'unassigned') {
        if (t.responsable !== null && t.responsable !== undefined) return false;
      } else {
        if (t.responsable !== filters.asignadoId) return false;
      }
    }

    if (filters.autorizadorId !== null) {
      if (filters.autorizadorId === 'unassigned') {
        if (t.idAutorizadoInt !== null && t.idAutorizadoInt !== undefined) return false;
      } else {
        if (t.idAutorizadoInt !== filters.autorizadorId) return false;
      }
    }

    if (filters.authStatus === 'authorized' && t.tgAutorizadoInt !== true) return false;
    if (filters.authStatus === 'unauthorized' && t.tgAutorizadoInt === true) return false;

    // Date filter (Plan B: only active when filterYear is set)
    if (filters.filterYear !== null) {
      const dateStr = (t as any)[filters.dateField];
      if (!dateStr) return false; // tasks without that date type are excluded
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return false;
      if (date.getFullYear() !== filters.filterYear) return false;
      if (filters.filterMonth !== null && date.getMonth() + 1 !== filters.filterMonth) return false;
    }

    return true;
  });
}